// Animated Sri Yantra-inspired ornament
export const Yantra = ({ size = 480, className = "" }) => (
  <svg viewBox="0 0 500 500" width={size} height={size} className={className} aria-hidden="true">
    <defs>
      <radialGradient id="yantra-bg" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#1e1a42" />
        <stop offset="60%" stopColor="#0e0b28" />
        <stop offset="100%" stopColor="#050311" />
      </radialGradient>
      <radialGradient id="yantra-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#E8562B" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#E8562B" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* Outer square frame */}
    <rect x="30" y="30" width="440" height="440" fill="url(#yantra-bg)" stroke="#C9962B" strokeWidth="0.8" />
    <rect x="50" y="50" width="400" height="400" fill="none" stroke="#C9962B" strokeWidth="0.5" opacity="0.6" />

    {/* Gateway marks (T-shaped gopurams) on each side */}
    {[
      [250, 30], [250, 470], [30, 250], [470, 250],
    ].map(([cx, cy], i) => (
      <g key={i}>
        <rect x={cx - 20} y={cy - 6} width="40" height="12" fill="#C9962B" opacity="0.6"
          transform={i < 2 ? "" : `rotate(90 ${cx} ${cy})`} />
      </g>
    ))}

    {/* Rotating outer lotus petals */}
    <g className="animate-slow-rotate" style={{ transformOrigin: "250px 250px" }}>
      {[...Array(16)].map((_, i) => {
        const angle = (i * 22.5) * (Math.PI / 180);
        const x = 250 + Math.cos(angle) * 175;
        const y = 250 + Math.sin(angle) * 175;
        return (
          <path
            key={i}
            d={`M ${x} ${y} Q ${250 + Math.cos(angle + 0.15) * 195} ${250 + Math.sin(angle + 0.15) * 195} ${250 + Math.cos(angle + 0.3) * 175} ${250 + Math.sin(angle + 0.3) * 175}`}
            fill="none"
            stroke="#C9962B"
            strokeWidth="0.6"
            opacity="0.7"
          />
        );
      })}
      <circle cx="250" cy="250" r="175" fill="none" stroke="#C9962B" strokeWidth="0.5" opacity="0.3" />
      <circle cx="250" cy="250" r="150" fill="none" stroke="#C9962B" strokeWidth="0.5" opacity="0.4" />
    </g>

    {/* Inner rotating — 8 petals */}
    <g className="animate-slow-rotate-reverse" style={{ transformOrigin: "250px 250px" }}>
      {[...Array(8)].map((_, i) => {
        const angle = (i * 45 - 90) * (Math.PI / 180);
        const r1 = 110;
        const r2 = 140;
        const x1 = 250 + Math.cos(angle) * r1;
        const y1 = 250 + Math.sin(angle) * r1;
        const x2 = 250 + Math.cos(angle + 0.25) * r2;
        const y2 = 250 + Math.sin(angle + 0.25) * r2;
        const x3 = 250 + Math.cos(angle + 0.5) * r1;
        const y3 = 250 + Math.sin(angle + 0.5) * r1;
        return (
          <path
            key={i}
            d={`M ${x1} ${y1} Q ${x2} ${y2} ${x3} ${y3}`}
            fill="none"
            stroke="#E6B84A"
            strokeWidth="0.8"
            opacity="0.6"
          />
        );
      })}
    </g>

    {/* Nine interlocking triangles (Sri Yantra approximation) */}
    <g className="animate-pulse-glow">
      {/* 4 upward triangles (Shiva) */}
      {[
        { size: 100, y: 10 },
        { size: 80, y: 20 },
        { size: 60, y: 30 },
        { size: 40, y: 35 },
      ].map((t, i) => {
        const h = t.size * Math.sqrt(3);
        return (
          <polygon
            key={`up-${i}`}
            points={`250,${250 - h / 2 + t.y} ${250 - t.size},${250 + h / 2 + t.y} ${250 + t.size},${250 + h / 2 + t.y}`}
            fill="none"
            stroke="#E8562B"
            strokeWidth="0.8"
            opacity="0.85"
          />
        );
      })}
      {/* 5 downward triangles (Shakti) */}
      {[
        { size: 110, y: -5 },
        { size: 90, y: -15 },
        { size: 70, y: -25 },
        { size: 50, y: -30 },
        { size: 30, y: -32 },
      ].map((t, i) => {
        const h = t.size * Math.sqrt(3);
        return (
          <polygon
            key={`dn-${i}`}
            points={`250,${250 + h / 2 + t.y} ${250 - t.size},${250 - h / 2 + t.y} ${250 + t.size},${250 - h / 2 + t.y}`}
            fill="none"
            stroke="#E8562B"
            strokeWidth="0.8"
            opacity="0.75"
          />
        );
      })}
    </g>

    {/* Bindu - center point */}
    <circle cx="250" cy="250" r="40" fill="url(#yantra-glow)" />
    <circle cx="250" cy="250" r="10" fill="#E8562B" />
    <circle cx="250" cy="250" r="4" fill="#F7E7C4" />

    {/* Small stars scattered */}
    {[...Array(22)].map((_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const r = 50 + Math.random() * 100;
      return (
        <circle
          key={`s-${i}`}
          cx={250 + Math.cos(angle) * r}
          cy={250 + Math.sin(angle) * r}
          r={0.8 + Math.random() * 1.2}
          fill="#F7E7C4"
          opacity={0.3 + Math.random() * 0.5}
          className="animate-twinkle"
          style={{ animationDelay: `${i * 0.2}s` }}
        />
      );
    })}
  </svg>
);

// Ornamental rule — gold diamond between hairlines
export const OrnamentDivider = ({ className = "" }) => (
  <div className={`flex items-center gap-6 ${className}`}>
    <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, #C9962B 50%, transparent)" }} />
    <svg width="48" height="20" viewBox="0 0 48 20" aria-hidden="true">
      <g fill="#C9962B">
        <circle cx="6" cy="10" r="1.5" />
        <circle cx="14" cy="10" r="2" />
        <path d="M24 4 L30 10 L24 16 L18 10 Z" />
        <circle cx="34" cy="10" r="2" />
        <circle cx="42" cy="10" r="1.5" />
      </g>
    </svg>
    <div className="flex-1 h-px" style={{ background: "linear-gradient(90deg, transparent, #C9962B 50%, transparent)" }} />
  </div>
);

// Small mandala accent (for eyebrows or page decorations)
export const MandalaMark = ({ size = 36, color = "#C9962B", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" className={className} aria-hidden="true">
    <g fill="none" stroke={color} strokeWidth="0.8">
      <circle cx="20" cy="20" r="18" opacity="0.3" />
      <circle cx="20" cy="20" r="12" opacity="0.5" />
      <circle cx="20" cy="20" r="4" />
      {[...Array(8)].map((_, i) => {
        const a = (i * 45) * (Math.PI / 180);
        return <line key={i} x1={20 + Math.cos(a) * 4} y1={20 + Math.sin(a) * 4} x2={20 + Math.cos(a) * 18} y2={20 + Math.sin(a) * 18} />;
      })}
      {[...Array(8)].map((_, i) => {
        const a = (i * 45 + 22.5) * (Math.PI / 180);
        return <circle key={`d-${i}`} cx={20 + Math.cos(a) * 12} cy={20 + Math.sin(a) * 12} r="1.2" fill={color} />;
      })}
    </g>
  </svg>
);

// Temple arch frame (SVG)
export const ArchFrame = ({ children, className = "" }) => (
  <div className={`relative ${className}`}>
    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 500" preserveAspectRatio="none" aria-hidden="true">
      <path
        d="M20,500 L20,200 Q20,20 200,20 Q380,20 380,200 L380,500 Z"
        fill="none"
        stroke="#C9962B"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
    {children}
  </div>
);

// Small Om glyph component
export const OmGlyph = ({ size = 24, className = "" }) => (
  <span className={`font-sanskrit ${className}`} style={{ fontSize: size, lineHeight: 1, color: "var(--terracotta)" }}>
    ॐ
  </span>
);

// Om glyph cradled inside a hand-inked halo that thickens as the user
// completes Academy lessons. Zero lessons → no halo (bare OmGlyph is
// returned unchanged). Halo geometry follows the illuminated-manuscript
// contract: vermillion hairlines, gold accents, no glow, no gradient.
//
// Tiers:
//   0        · no ring
//   1-3      · single dashed vermillion ring (kindled)
//   4-6      · solid vermillion ring + 4 cardinal dots (warmed)
//   7-9      · vermillion ring + outer gold ring + 8 dots (deepened)
//   10 (all) · dual gold rings, breathing pulse (complete)
export const OmHalo = ({ depth = 0, size = 24, className = "" }) => {
  const d = Math.max(0, Math.min(10, depth | 0));
  if (d === 0) return <OmGlyph size={size} className={className} />;

  const box = size * 2.2;                 // room for the outer ring
  const c   = box / 2;
  const rV  = size * 0.86;                // vermillion ring radius
  const rG  = size * 1.02;                // outer gold ring radius
  const rDots = size * 0.86;

  const tier =
    d >= 10 ? "complete" :
    d >= 7  ? "deepened" :
    d >= 4  ? "warmed"   :
              "kindled";

  const dotCount = tier === "warmed" ? 4 : (tier === "deepened" || tier === "complete") ? 8 : 0;
  const dots = Array.from({ length: dotCount }, (_, i) => {
    const angle = (i / dotCount) * Math.PI * 2 - Math.PI / 2;
    return { x: c + rDots * Math.cos(angle), y: c + rDots * Math.sin(angle) };
  });

  return (
    <span
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: box, height: box }}
      data-testid="om-halo"
      data-halo-tier={tier}
      data-halo-depth={d}
      title={
        tier === "complete"
          ? "You have walked the full Academy"
          : `Academy: ${d} of 10 lessons kindled`
      }
    >
      <svg
        width={box}
        height={box}
        viewBox={`0 0 ${box} ${box}`}
        className="absolute inset-0"
        aria-hidden="true"
        style={tier === "complete" ? { animation: "om-halo-breathe 4.2s ease-in-out infinite" } : undefined}
      >
        {/* Vermillion ring (always present when d>=1) */}
        <circle
          cx={c} cy={c} r={rV}
          fill="none"
          stroke="var(--terracotta, #C7402A)"
          strokeWidth="0.9"
          opacity={tier === "kindled" ? 0.55 : 0.85}
          strokeDasharray={tier === "kindled" ? "2 3" : undefined}
        />
        {/* Outer gold ring (deepened + complete) */}
        {(tier === "deepened" || tier === "complete") && (
          <circle
            cx={c} cy={c} r={rG}
            fill="none"
            stroke="var(--hairline-gold, #C9962B)"
            strokeWidth="0.6"
            opacity={0.75}
          />
        )}
        {/* Innermost gold hairline (complete only) */}
        {tier === "complete" && (
          <circle
            cx={c} cy={c} r={rV - 3}
            fill="none"
            stroke="var(--hairline-gold, #C9962B)"
            strokeWidth="0.5"
            opacity={0.5}
          />
        )}
        {/* Cardinal / intercardinal dots */}
        {dots.map((p, i) => (
          <circle
            key={i}
            cx={p.x} cy={p.y} r={0.9}
            fill="var(--hairline-gold, #C9962B)"
            opacity={0.85}
          />
        ))}
      </svg>
      <OmGlyph size={size} />
      <style>{`
        @keyframes om-halo-breathe {
          0%, 100% { opacity: 0.85; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.03); }
        }
      `}</style>
    </span>
  );
};

// Ornamental corner (for cards / sections)
export const CornerOrnament = ({ corner = "tl", size = 40, color = "#C9962B" }) => {
  const rotations = { tl: 0, tr: 90, br: 180, bl: 270 };
  return (
    <svg
      width={size} height={size} viewBox="0 0 40 40"
      className="absolute"
      style={{ transform: `rotate(${rotations[corner]}deg)`, [corner.includes("t") ? "top" : "bottom"]: 0, [corner.includes("l") ? "left" : "right"]: 0 }}
      aria-hidden="true"
    >
      <g fill="none" stroke={color} strokeWidth="1">
        <path d="M2,2 L16,2 M2,2 L2,16" />
        <path d="M6,6 Q10,10 16,6" opacity="0.5" />
        <circle cx="6" cy="6" r="1.5" fill={color} />
      </g>
    </svg>
  );
};
